import { CircularProgress } from '@shared/components/plugin-components';
import { Text } from '@shared/components/text';
import { RateMetadata } from '@shared/types/quote';
import { useEffect, useRef } from 'react';
import { DATRateWidget } from '../DATRateWidget';
import { RateError } from '../rating-widgets/components/RateError/RateError';
import { useDATHistoryRatesMutation } from './hooks/useDATHistoryRatesMutation';

type DATHistoryRatesContainerProps = {
  quoteId: string;
  expanded: boolean;
};

export const DATHistoryRatesContainer = ({
  quoteId,
  expanded,
}: DATHistoryRatesContainerProps) => {
  const { fetchDATHistoryRatesMutation, historyRates, loading } =
    useDATHistoryRatesMutation(quoteId);
  const hasRequestedRef = useRef(false);

  useEffect(() => {
    if (expanded && !loading && !hasRequestedRef.current) {
      // NOTE(parlato): Lazy-fetch DAT history only once the user
      // expands the panel, mirroring market-conditions behavior so
      // automation rating stays lightweight.
      hasRequestedRef.current = true;
      fetchDATHistoryRatesMutation();
    }
  }, [expanded, loading]);

  if (loading && !historyRates) {
    return (
      <div className="flex w-full items-center justify-center border-t p-4">
        <CircularProgress size="sm" />
      </div>
    );
  }

  if (!historyRates) {
    return null;
  }

  if (historyRates.errorMessage) {
    return (
      <RateError
        errorMessage={historyRates.errorMessage}
        integrationName="DAT"
        fetchedEntity="history rates"
      />
    );
  }

  const metadata = historyRates.metadata as RateMetadata | undefined;
  if (!metadata) {
    return null;
  }

  return (
    <div className="flex w-full flex-col gap-1 border-t border-gray-200 bg-white px-4 py-3">
      <Text type="body-xs">{historyRates.label || 'History'}</Text>
      <DATRateWidget metadata={metadata} />
    </div>
  );
};
